import { Brand } from '@/constants/theme';
import { useAuth } from '@/hooks/use-auth';
import { getOnboardingCompleted } from '@/lib/onboarding-storage';
import { parseInviteTokenFromInput, PENDING_INVITE_KEY } from '@/lib/invite-links';
import { supabase } from '@/lib/supabase';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import * as Linking from 'expo-linking';
import { Redirect, useRouter, usePathname, useSegments } from 'expo-router';
import { useEffect, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';

function extractInviteToken(url: string): string | null {
  const token = parseInviteTokenFromInput(url);
  return token || null;
}

declare const window: {
  location?: { hash: string; search: string; pathname: string };
} | undefined;

const PUBLIC_PATHS = ['/privacy'];

/** Authenticated-only routes that are not under (tabs). Don't redirect to (tabs) when user is on these. */
const AUTHENTICATED_STANDALONE_PATHS = [
  '/delete-account',
  '/change-email',
  '/change-password',
  '/join-invite',
  '/invite',
  '/create-household',
];

export default function Index() {
  const { isAuthenticated, loading, user } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const segments = useSegments();
  const isOnPublicPath =
    pathname != null && PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const isOnAuthenticatedStandalonePath =
    pathname != null &&
    AUTHENTICATED_STANDALONE_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

  const [deepLinkTarget, setDeepLinkTarget] = useState<string | null>(null);
  const [pendingInviteFromLink, setPendingInviteFromLink] = useState(false);
  const [pendingInviteChecked, setPendingInviteChecked] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  useEffect(() => {
    const handleDeepLink = (event: { url: string }) => {
      const url = event.url;
      const inviteToken = extractInviteToken(url);
      if (inviteToken) {
        AsyncStorage.setItem(PENDING_INVITE_KEY, inviteToken);
        setPendingInviteFromLink(true);
        return;
      }
      if (url.includes('reset-password') || (url.includes('access_token') && url.includes('type=recovery'))) {
        setDeepLinkTarget('/(auth)/reset-password');
      } else if (url.includes('verify-email') || url.includes('type=signup') || url.includes('type=email')) {
        setDeepLinkTarget('/(auth)/verify-email');
      }
    };

    const subscription = Linking.addEventListener('url', handleDeepLink);

    const checkInitialUrl = () => {
      if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
        const { pathname, hash, search } = window.location;
        const fullUrl = window.location.href;
        // Don't run invite/deep-link handling on public pages (e.g. /privacy)
        if (PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
          return;
        }
        if (pathname.includes('/invite/')) {
          const token = parseInviteTokenFromInput(fullUrl);
          if (token) {
            AsyncStorage.setItem(PENDING_INVITE_KEY, token);
            setPendingInviteFromLink(true);
            return;
          }
        }
        if (
          pathname.includes('/verify-email') ||
          hash.includes('type=signup') ||
          hash.includes('type=email') ||
          search.includes('type=signup') ||
          search.includes('type=email')
        ) {
          setDeepLinkTarget('/(auth)/verify-email');
          return;
        }
        if (
          pathname.includes('/reset-password') ||
          (hash.includes('access_token') && hash.includes('type=recovery'))
        ) {
          setDeepLinkTarget('/(auth)/reset-password');
          return;
        }
      }

      Linking.getInitialURL().then((url) => {
        if (!url) return;
        const inviteToken = extractInviteToken(url);
        if (inviteToken) {
          AsyncStorage.setItem(PENDING_INVITE_KEY, inviteToken);
          setPendingInviteFromLink(true);
          return;
        }
        if (url.includes('reset-password') || (url.includes('access_token') && url.includes('type=recovery'))) {
          setDeepLinkTarget('/(auth)/reset-password');
        } else if (url.includes('verify-email') || url.includes('type=signup') || url.includes('type=email')) {
          setDeepLinkTarget('/(auth)/verify-email');
        }
      }).catch(() => {});
    };

    const timeoutId = setTimeout(checkInitialUrl, 0);

    return () => {
      clearTimeout(timeoutId);
      subscription.remove();
    };
  }, []);

  useEffect(() => {
    if (deepLinkTarget) return;
    if (loading) return;

    const inAuthGroup = segments[0] === '(auth)';
    const inTabsGroup = segments[0] === '(tabs)';
    const inPublicGroup = segments[0] === '(public)' || isOnPublicPath;

    if (pendingInviteFromLink) {
      AsyncStorage.getItem(PENDING_INVITE_KEY).then(async (token) => {
        setPendingInviteFromLink(false);
        if (token) {
          if (isAuthenticated) {
            if (user?.id) {
              const completed = await getOnboardingCompleted(user.id);
              if (!completed) {
                router.replace('/(auth)/onboarding');
                return;
              }
              let hasHousehold = false;
              try {
                const { data: profile } = await supabase
                  .from('profiles')
                  .select('household_id')
                  .eq('id', user.id)
                  .maybeSingle() as { data: { household_id: string | null } | null };
                hasHousehold = !!profile?.household_id;
              } catch {
                // On error, allow join-invite so user can choose
              }
              await AsyncStorage.removeItem(PENDING_INVITE_KEY);
              if (hasHousehold) {
                setDeepLinkTarget('/(tabs)');
              } else {
                setDeepLinkTarget(`/join-invite?token=${encodeURIComponent(token)}`);
              }
            } else {
              router.replace('/(auth)/onboarding');
            }
          } else {
            router.replace('/(auth)/sign-in');
          }
        }
      });
      return;
    }

    if (!isAuthenticated && !inAuthGroup && !inPublicGroup) {
      router.replace('/(auth)/sign-in');
    } else if (
      isAuthenticated &&
      !inTabsGroup &&
      !inAuthGroup &&
      !inPublicGroup &&
      !isOnAuthenticatedStandalonePath
    ) {
      if (!pendingInviteChecked || !onboardingChecked) {
        const run = async () => {
          if (!user?.id) return;
          setPendingInviteChecked(true);
          setOnboardingChecked(true);
          const completed = await getOnboardingCompleted(user.id);
          if (!completed) {
            router.replace('/(auth)/onboarding');
            return;
          }
          const token = await AsyncStorage.getItem(PENDING_INVITE_KEY);
          if (token) {
            let hasHousehold = false;
            try {
              const { data: profile } = await supabase
                .from('profiles')
                .select('household_id')
                .eq('id', user.id)
                .maybeSingle() as { data: { household_id: string | null } | null };
              hasHousehold = !!profile?.household_id;
            } catch {
              // On error, send to join-invite so user can choose
            }
            await AsyncStorage.removeItem(PENDING_INVITE_KEY);
            if (hasHousehold) {
              router.replace('/(tabs)');
            } else {
              router.replace(
                `/join-invite?token=${encodeURIComponent(token)}` as any
              );
            }
          } else {
            AsyncStorage.removeItem(PENDING_INVITE_KEY);
            router.replace('/(tabs)');
          }
        };
        run();
      }
    }
  }, [
    deepLinkTarget,
    isAuthenticated,
    isOnAuthenticatedStandalonePath,
    isOnPublicPath,
    loading,
    segments,
    router,
    pendingInviteFromLink,
    pendingInviteChecked,
    onboardingChecked,
    user?.id,
  ]);

  useEffect(() => {
    if (isAuthenticated && (segments[0] === '(tabs)' || segments[0] === 'join-invite')) {
      setPendingInviteChecked(true);
      setOnboardingChecked(true);
    }
    if (!isAuthenticated) {
      setPendingInviteChecked(false);
      setOnboardingChecked(false);
    }
  }, [isAuthenticated, segments]);

  if (deepLinkTarget) {
    return <Redirect href={deepLinkTarget as any} />;
  }

  if (loading) {
    return (
      <View style={styles.container}>
        <LinearGradient
          colors={[...Brand.primaryGradient]}
          style={styles.background}
        />
        <ActivityIndicator size="large" color={Brand.primary} />
      </View>
    );
  }

  // If URL is a public path but we're still on index (e.g. initialRouteName), send router to the public page
  if (isOnPublicPath && pathname) {
    return <Redirect href={pathname as any} />;
  }

  return null;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
});
